// attribution.js — Return attribution waterfall decomposition

/**
 * Calculate return attribution from yearly projection data.
 * Decomposes total return into carry, FX impact, forward cost, fees, taxes, and withdrawal drag.
 * @param {Object[]} years - array of yearly results from runProjection scenario
 * @param {number} initialValue - starting portfolio value in base currency
 * @returns {Object} attribution breakdown
 */
export function calcAttribution(years, initialValue) {
  if (!years || years.length < 2 || !initialValue || initialValue <= 0) {
    return {
      startingValue: 0,
      carry: 0,
      fxImpact: 0,
      forwardCost: 0,
      fees: 0,
      taxes: 0,
      withdrawalDrag: 0,
      endingValue: 0,
      totalReturn: 0,
      totalReturnPct: 0,
    };
  }

  let carry = 0;
  let fxImpact = 0;
  let forwardCost = 0;
  let fees = 0;
  let taxes = 0;
  let withdrawalDrag = 0;

  // Skip year 0 (initial state)
  for (let i = 1; i < years.length; i++) {
    const y = years[i];
    carry += y.income || 0;
    fxImpact += y.fxImpact || 0;
    forwardCost += Math.abs(y.forwardCost || 0);
    fees += y.fees || 0;
    taxes += y.taxes || 0;
    withdrawalDrag += y.withdrawal || 0;
  }

  const endingValue = years[years.length - 1].valueHedged || 0;
  const totalReturn = endingValue - initialValue;
  const totalReturnPct = initialValue > 0 ? totalReturn / initialValue : 0;

  return {
    startingValue: initialValue,
    carry,
    fxImpact,
    forwardCost,
    fees,
    taxes,
    withdrawalDrag,
    endingValue,
    totalReturn,
    totalReturnPct,
  };
}

/**
 * Build waterfall chart data from attribution.
 * Each step has a start and end value for rendering stacked bars.
 * @param {Object} attribution - output from calcAttribution
 * @returns {Object[]} array of waterfall steps
 */
export function buildWaterfallData(attribution) {
  const steps = [];
  let running = attribution.startingValue;

  steps.push({
    label: 'Starting Value',
    value: attribution.startingValue,
    start: 0,
    end: attribution.startingValue,
    type: 'total',
  });

  // Carry (positive)
  steps.push({
    label: 'Carry Income',
    value: attribution.carry,
    start: running,
    end: running + attribution.carry,
    type: 'positive',
  });
  running += attribution.carry;

  // FX Impact (can be positive or negative)
  steps.push({
    label: 'Foreign Exchange Impact',
    value: attribution.fxImpact,
    start: attribution.fxImpact >= 0 ? running : running + attribution.fxImpact,
    end: attribution.fxImpact >= 0 ? running + attribution.fxImpact : running,
    type: attribution.fxImpact >= 0 ? 'positive' : 'negative',
  });
  running += attribution.fxImpact;

  // Forward Cost (negative)
  steps.push({
    label: 'Forward Cost',
    value: -attribution.forwardCost,
    start: running - attribution.forwardCost,
    end: running,
    type: 'negative',
  });
  running -= attribution.forwardCost;

  // Fees (negative)
  steps.push({
    label: 'Fees',
    value: -attribution.fees,
    start: running - attribution.fees,
    end: running,
    type: 'negative',
  });
  running -= attribution.fees;

  // Taxes (negative)
  steps.push({
    label: 'Taxes',
    value: -attribution.taxes,
    start: running - attribution.taxes,
    end: running,
    type: 'negative',
  });
  running -= attribution.taxes;

  // Withdrawals (negative)
  steps.push({
    label: 'Withdrawals',
    value: -attribution.withdrawalDrag,
    start: running - attribution.withdrawalDrag,
    end: running,
    type: 'negative',
  });
  running -= attribution.withdrawalDrag;

  // End Value
  steps.push({
    label: 'End Value',
    value: attribution.endingValue,
    start: 0,
    end: attribution.endingValue,
    type: 'total',
  });

  return steps;
}
